import type { StatisticsResponse } from "@/api";
import {
  endOfMonth,
  endOfQuarter,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { Utility } from "./utility";

export interface Statistics extends StatisticsResponse {}

export enum StatisticsDateFilters {
  all = "All Dates",
  today = "Today",
  yesterday = "Yesterday",
  this_week = "This Week",
  this_month = "This Month",
  this_quarter = "This Quarter",
  this_year = "This Year",
  last_week = "Last Week",
  last_month = "Last Month",
  last_quarter = "Last Quarter",
  last_year = "Last Year",
}

export class Statistics extends Utility<Statistics> {
  constructor(init?: Partial<Statistics>) {
    super();
    Object.assign(this, init);
  }

  public after(dateFilter: StatisticsDateFilters): string | null {
    let date: Date | null = null;

    switch (dateFilter) {
      case StatisticsDateFilters.today:
        date = startOfToday();
        break;
      case StatisticsDateFilters.yesterday:
        date = startOfYesterday();
        break;
      case StatisticsDateFilters.this_week:
        date = startOfWeek(new Date());
        break;
      case StatisticsDateFilters.this_month:
        date = startOfMonth(new Date());
        break;
      case StatisticsDateFilters.this_quarter:
        date = startOfQuarter(new Date());
        break;
      case StatisticsDateFilters.this_year:
        date = startOfYear(new Date());
        break;
      case StatisticsDateFilters.last_week:
        date = startOfWeek(subWeeks(new Date(), 1));
        break;
      case StatisticsDateFilters.last_month:
        date = startOfMonth(subMonths(new Date(), 1));
        break;
      case StatisticsDateFilters.last_quarter:
        date = startOfQuarter(subQuarters(new Date(), 1));
        break;
      case StatisticsDateFilters.last_year:
        date = startOfYear(subYears(new Date(), 1));
        break;
    }

    return date ? date.toISOString() : null;
  }

  public before(dateFilter: StatisticsDateFilters): string | null {
    let date: Date | null = null;

    switch (dateFilter) {
      case StatisticsDateFilters.today:
        date = endOfToday();
        break;
      case StatisticsDateFilters.yesterday:
        date = endOfYesterday();
        break;
      case StatisticsDateFilters.this_week:
        date = endOfWeek(new Date());
        break;
      case StatisticsDateFilters.this_month:
        date = endOfMonth(new Date());
        break;
      case StatisticsDateFilters.this_quarter:
        date = endOfQuarter(new Date());
        break;
      case StatisticsDateFilters.this_year:
        date = endOfYear(new Date());
        break;
      case StatisticsDateFilters.last_week:
        date = endOfWeek(subWeeks(new Date(), 1));
        break;
      case StatisticsDateFilters.last_month:
        date = endOfMonth(subMonths(new Date(), 1));
        break;
      case StatisticsDateFilters.last_quarter:
        date = endOfQuarter(subQuarters(new Date(), 1));
        break;
      case StatisticsDateFilters.last_year:
        date = endOfYear(subYears(new Date(), 1));
        break;
    }

    return date ? date.toISOString() : null;
  }

  public get totalCredits() {
    return 10000;
  }

  public get consumptionRates() {
    return {
      interviewEval: 1,
      phoneInterview: 2.5,
      resumeScreening: 1,
      chatMessage: 0.1,
    };
  }

  public get allocatedCredits() {
    return {
      interviewEval: 350,
      phoneInterview: 9000,
      resumeScreening: 250,
      chatMessage: 350,
      others: 50,
    };
  }

  public get creditCaps() {
    return {
      interviewEval:
        this.allocatedCredits.interviewEval /
        this.consumptionRates.interviewEval,
      phoneInterview:
        this.allocatedCredits.phoneInterview /
        this.consumptionRates.phoneInterview,
      resumeScreening:
        this.allocatedCredits.resumeScreening /
        this.consumptionRates.resumeScreening,
      chatMessage:
        this.allocatedCredits.chatMessage / this.consumptionRates.chatMessage,
    };
  }

  public get creditChartData() {
    const totalCredits = this.totalCredits;
    let creditChartData = {
      interviewEval: 0,
      phoneInterview: 0,
      resumeScreening: 0,
      chatMessage: 0,
      usedCredits: 0,
      remainingCredits: 0,
    };

    if (this.call_count && this.call_count.total) {
      const interviewEvalCredits =
        this.call_count.total * this.consumptionRates.interviewEval;
      if (interviewEvalCredits > this.allocatedCredits.interviewEval) {
        creditChartData.interviewEval = this.allocatedCredits.interviewEval;
      } else {
        creditChartData.interviewEval = interviewEvalCredits;
      }
    }
    if (this.call_duration && this.call_duration.total) {
      const phoneInterviewCredits =
        this.call_duration.total * this.consumptionRates.phoneInterview;
      if (phoneInterviewCredits > this.allocatedCredits.phoneInterview) {
        creditChartData.phoneInterview = this.allocatedCredits.phoneInterview;
      } else {
        creditChartData.phoneInterview = phoneInterviewCredits;
      }
    }
    if (this.resume_count && this.resume_count.total) {
      const resumeScreeningCredits =
        this.resume_count.total * this.consumptionRates.resumeScreening;
      if (resumeScreeningCredits > this.allocatedCredits.resumeScreening) {
        creditChartData.resumeScreening = this.allocatedCredits.resumeScreening;
      } else {
        creditChartData.resumeScreening = resumeScreeningCredits;
      }
    }
    if (this.message_count && this.message_count.total) {
      const chatMessageCredits =
        this.message_count.total * this.consumptionRates.chatMessage;
      if (chatMessageCredits > this.allocatedCredits.chatMessage) {
        creditChartData.chatMessage = this.allocatedCredits.chatMessage;
      } else {
        creditChartData.chatMessage = chatMessageCredits;
      }
    }

    creditChartData.usedCredits =
      creditChartData.interviewEval +
      creditChartData.phoneInterview +
      creditChartData.resumeScreening +
      creditChartData.chatMessage;
    creditChartData.remainingCredits =
      totalCredits - creditChartData.usedCredits;

    return creditChartData;
  }
}
